/**
 * Taksasi Utility Functions
 * 
 * Helper functions for Taksasi module
 */

import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { TaksasiCompanyGroup, TaksasiItem, TaksasiStats } from '../types';
import * as XLSX from 'xlsx-js-style';
import { trackDownload } from '../../../activity';

/**
 * Format date for API (YYYY-MM-DD)
 */
export const formatDateForAPI = (date: Date | string): string => {
  if (typeof date === 'string') {
    return date;
  }
  return format(date, 'yyyy-MM-dd');
};

/**
 * Format date for display (DD/MM/YYYY)
 */
export const formatDateForDisplay = (date: string): string => {
  if (!date) return '-';
  try {
    return format(new Date(date), 'dd/MM/yyyy', { locale: idLocale });
  } catch {
    return date;
  }
};

/**
 * Format date cleanly without timezone offset (DD/MM/YYYY HH:mm)
 * Parses ISO date string and extracts date and time without timezone offset
 * Example: "2026-03-31T15:23:13.597142Z" -> "31/03/2026 15:23"
 */
export const formatDateClean = (date: string): string => {
  if (!date) return '-';
  try {
    const parts = date.split('T'); // Split date and time
    const dateOnly = parts[0]; // YYYY-MM-DD
    if (!dateOnly) return '-';
    
    let time = '';
    if (parts[1]) {
      const timePart = parts[1].split('.')[0]; // Get HH:mm:ss, remove milliseconds
      const [hour, minute] = timePart.split(':'); // Extract HH:mm
      time = ` ${hour}:${minute}`;
    }
    
    const formattedDate = dateOnly.split('-').reverse().join('/'); // Convert to DD/MM/YYYY
    return formattedDate + time;
  } catch {
    return date;
  }
};

/**
 * Format date with time for display (DD/MM/YYYY HH:mm)
 */
export const formatDateTimeForDisplay = (date: string): string => {
  if (!date) return '-';
  try {
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: idLocale });
  } catch {
    return date;
  }
};

/**
 * Format number with decimal places
 */
export const formatNumber = (num: number) => {
      if (num === 0 || num === null || num === undefined) {
          return '-';
      }
      return new Intl.NumberFormat('id-ID', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
      }).format(num);
  };

export const formatNumber2Decimals = (num: number) => {
  if (num === 0 || num === null || num === undefined) {
    return '-';
  }
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

export const formatNumber1Decimal = (num: number) => {
  if (num === 0 || num === null || num === undefined) {
    return '-';
  }
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(num);
};
/**
 * Format number as integer (no decimals)
 */
export const formatInteger = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('id-ID', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

/**
 * Format pokok numbers with line break every 5 items
 * Converts "1, 2, 3, 4, 5, 6, 7, 8, 9, 10" to "1, 2, 3, 4, 5\n6, 7, 8, 9, 10"
 */
export const formatPokok = (pokok_nbrs: string | null | undefined): string => {
  if (!pokok_nbrs) return '-';
  
  const items = pokok_nbrs.split(',').map(item => item.trim());
  const chunks: string[] = [];
  
  for (let i = 0; i < items.length; i += 5) {
    chunks.push(items.slice(i, i + 5).join(', '));
  }
  
  return chunks.join('\n');
};

/**
 * Calculate statistics from Taksasi data
 */
export const calculateStats = (data: TaksasiCompanyGroup[]): TaksasiStats => {
  if (!data || data.length === 0) {
    return {
      total_records: 0,
      total_planted_area: 0,
      total_est_output: 0,
      avg_bjr: 0,
      avg_akp: 0,
      total_est_ripe_bunch: 0,
    };
  }

  const allItems: TaksasiItem[] = [];
  data.forEach(company => {
    company.estates.forEach(estate => {
      estate.divisions.forEach(division => {
        allItems.push(...division.items);
      });
    });
  });

  const totalPlantedArea = allItems.reduce((sum, item) => sum + (item.planted_area || 0), 0);
  const totalEstOutput = allItems.reduce((sum, item) => sum + (item.est_output || 0), 0);
  const totalBjr = allItems.reduce((sum, item) => sum + (item.bjr || 0), 0);
  const totalAkp = allItems.reduce((sum, item) => sum + (item.akp || 0), 0);
  const totalEstRipeBunch = allItems.reduce((sum, item) => sum + (item.est_ripe_bunch || 0), 0);

  return {
    total_records: allItems.length,
    total_planted_area: totalPlantedArea,
    total_est_output: totalEstOutput,
    avg_bjr: allItems.length > 0 ? totalBjr / allItems.length : 0,
    avg_akp: allItems.length > 0 ? totalAkp / allItems.length : 0,
    total_est_ripe_bunch: totalEstRipeBunch,
  };
};

/**
 * Export data to Excel format
 */
export const exportToExcel = (data: TaksasiCompanyGroup[], filename = 'taksasi-export.xlsx'): void => {
  if (!data || data.length === 0) {
    return;
  }

  // Flatten data dari CompanyGroup → EstateGroup structure
  const allItems: TaksasiItem[] = [];
  data.forEach(company => {
    company.estates.forEach(estate => {
      estate.divisions.forEach(division => {
        allItems.push(...division.items);
      });
    });
  });

  const companyName = data.length > 0 ? data[0].company : '';

  const headers = [
    'Tanggal Dibuat',
    'Tanggal Panen',
    'Estate',
    'Divisi',
    'Blok',
    'Luas (Ha)',
    'Plant Total',
    'AKP',
    'Berat Dasar',
    'Est. Janjang',
    'BJR (kg)',
    'Est. Berat (kg)',
    'Est. HK',
    'Est. Output',
    'Dibuat Oleh',
  ];

  const rows = allItems.map(item => [
    formatDateClean(item.create_date),
    formatDateForDisplay(item.harvest_date),
    item.estate,
    item.division,
    item.block,
    item.planted_area || 0, 
    item.plant_total || 0,
    item.akp || 0,
    item.base_weight || 0,
    item.est_ripe_bunch || 0,
    item.bjr || 0,
    item.est_weight || 0,
    item.est_hk || 0,
    item.est_output || 0,
    item.create_by,
  ]);

  const wsData = [
    [`Company: ${companyName}`],
    [], // empty row for spacing
    headers,
    ...rows
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(wsData);

  // Title Style
  const titleStyle = {
    font: { bold: true, sz: 14, color: { rgb: '000000' } },
    alignment: { horizontal: 'left', vertical: 'center' },
  };

  // Header Style
  const headerStyle = {
    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
    fill: { fgColor: { rgb: '1A365D' } }, // Blue.700
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: 'CCCCCC' } },
      bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
      left: { style: 'thin', color: { rgb: 'CCCCCC' } },
      right: { style: 'thin', color: { rgb: 'CCCCCC' } },
    },
  };

  // Apply style to Company Title (Row 0, Col 0)
  const titleCell = XLSX.utils.encode_cell({ r: 0, c: 0 });
  if (worksheet[titleCell]) {
    worksheet[titleCell].s = titleStyle;
  }

  // Apply styes to headers (Row 2)
  for (let c = 0; c < headers.length; c++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 2, c });
    if (worksheet[cellAddress]) {
      worksheet[cellAddress].s = headerStyle;
    }
  }

  // Apply styles to data rows (Starts at Row 3)
  for (let r = 3; r < 3 + rows.length; r++) {
    for (let c = 0; c < headers.length; c++) {
      const cellAddress = XLSX.utils.encode_cell({ r, c });
      if (worksheet[cellAddress]) {
        const isNumberColumn = c >= 5 && c <= 13;
        worksheet[cellAddress].s = {
          font: { sz: 11 },
          alignment: { horizontal: isNumberColumn ? 'right' : 'left', vertical: 'center' },
          border: {
            top: { style: 'thin', color: { rgb: 'E2E8F0' } },
            bottom: { style: 'thin', color: { rgb: 'E2E8F0' } },
            left: { style: 'thin', color: { rgb: 'E2E8F0' } },
            right: { style: 'thin', color: { rgb: 'E2E8F0' } },
          },
        };
        if (isNumberColumn) {  
          worksheet[cellAddress].z = '#,##0.00';  
        }
      }
    }
  }

  // Set column widths
  worksheet['!cols'] = [
    { wch: 20 }, // Tanggal Dibuat
    { wch: 20 }, // Dibuat Oleh
    { wch: 15 }, // Tanggal Panen
    { wch: 20 }, // Estate
    { wch: 15 }, // Divisi
    { wch: 15 }, // Blok
    { wch: 15 }, // Luas Tanam (Ha)
    { wch: 15 }, // Total Tanaman
    { wch: 10 }, // AKP
    { wch: 15 }, // Bobot Dasar
    { wch: 20 }, // Est. Janjang Matang
    { wch: 10 }, // BJR (kg)
    { wch: 15 }, // Est. Berat (kg)
    { wch: 10 }, // Est. HK
    { wch: 15 }, // Est. Output
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Taksasi Data');

  XLSX.writeFile(workbook, filename);
  trackDownload('Laporan Taksasi', { filename });
};
